import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Archive, Calculator, ChevronRight, FileBarChart2, Globe2, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BATCH_STATUS_META, REGIONS, fmtRate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import CalculatorHub from "@/components/CalculatorHub";
import { NumInput } from "@/components/NumInput";

function NewBatchDrawer({ open, onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [region, setRegion] = useState("");
  const [rate, setRate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setRegion("");
      setRate("");
      setNotes("");
    }
  }, [open]);

  const submit = async () => {
    if (!name.trim() || !region || !(parseFloat(rate) > 0)) return;
    setSaving(true);
    try {
      await api.post("/batches", { name: name.trim(), region, exchange_rate_to_idr: parseFloat(rate), notes });
      toast.success(`Batch "${name.trim()}" created`);
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="font-heading">New PO Batch</DrawerTitle>
        </DrawerHeader>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto px-4 pb-8">
          <div className="space-y-2">
            <Label>Region</Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(REGIONS).map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`region-chip-${r.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setRegion(r)}
                  className={`h-12 rounded-md border text-sm font-semibold transition-colors active:scale-[0.98] ${
                    region === r ? "border-primary bg-primary text-white" : "bg-card text-stone-600 hover:bg-accent"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Currency (auto)</Label>
            <div data-testid="new-batch-currency" className="flex h-12 items-center rounded-md border bg-muted px-3 text-sm font-semibold text-stone-700">
              {region ? `${REGIONS[region].code} ${REGIONS[region].symbol}` : "Select a region first"}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Operational Exchange Rate to IDR (required)</Label>
            <NumInput
              data-testid="new-batch-rate-input"
              mode="decimal"
              className="h-12"
              placeholder={region ? `1 ${REGIONS[region].code} = Rp …` : "e.g. 110"}
              value={rate}
              onValue={setRate}
            />
            {region && parseFloat(rate) > 0 && (
              <p className="text-xs font-medium text-muted-foreground">{fmtRate(REGIONS[region].symbol, parseFloat(rate))}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Batch name</Label>
            <Input
              data-testid="new-batch-name-input"
              className="h-12"
              placeholder="e.g. Japan Aug 2026"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Input
              data-testid="new-batch-notes-input"
              className="h-12"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <Button data-testid="new-batch-submit-button" className="h-12 w-full text-base" onClick={submit} disabled={saving || !name.trim() || !region || !(parseFloat(rate) > 0)}>
            {saving ? "Creating…" : "Create Batch"}
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default function Batches() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [batches, setBatches] = useState(null);
  const [archiveCount, setArchiveCount] = useState(null);
  const [search, setSearch] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/batches");
      setBatches(data);
    } catch (e) {
      toast.error(apiError(e));
      setBatches([]);
    }
    try {
      const { data } = await api.get("/batches/archive");
      setArchiveCount(data.length);
    } catch {
      setArchiveCount(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const q = search.trim().toLowerCase();
  const matches = (name, date) =>
    !q ||
    (name || "").toLowerCase().includes(q) ||
    (date ? format(new Date(date), "d MMM yyyy").toLowerCase().includes(q) : false);
  const activeBatches = (batches || []).filter((b) => b.status !== "closed" && matches(b.name, b.created_at));

  return (
    <div data-testid="batches-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">PO Batches</h1>
          <p className="text-sm text-muted-foreground">Overseas purchasing trips</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            data-testid="calculator-hub-button"
            className="h-11 border border-[#EEDCE3] bg-white px-3 text-xs text-[#493D3B] hover:bg-[#FAF5F4] sm:h-12 sm:px-4 sm:text-sm"
            onClick={() => setCalcOpen(true)}
          >
            <Calculator className="h-4 w-4 text-[#C9828A]" /> USA / EURO Calculator
          </Button>
          {user.role === "owner" && (
            <Button data-testid="new-batch-button" className="h-11 px-3 text-xs sm:h-12 sm:px-5 sm:text-sm" onClick={() => setNewOpen(true)}>
              <Plus className="h-4 w-4" /> New Batch
            </Button>
          )}
        </div>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input
          data-testid="batch-search-input"
          className="h-12 pl-9"
          placeholder="Search batch name or date…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {batches === null ? (
        <div className="grid place-items-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : activeBatches.length === 0 ? (
        <div data-testid="batches-empty" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <div>
            <Globe2 className="mx-auto mb-3 h-10 w-10 text-stone-300" />
            <p className="font-medium text-muted-foreground">No active PO batches</p>
            {user.role === "owner" && <p className="text-sm text-muted-foreground">Create your first purchasing trip to get started.</p>}
          </div>
        </div>
      ) : (
        <div data-testid="batch-list" className="space-y-3">
          {activeBatches.map((b) => {
            const pct = b.ordered_units > 0 ? Math.min(100, (b.purchased_units / b.ordered_units) * 100) : 0;
            return (
              <div
                key={b.id}
                data-testid={`batch-card-${b.id}`}
                onClick={() => navigate(`/batches/${b.id}`)}
                className="w-full cursor-pointer rounded-md border bg-card p-4 text-left transition-transform hover:-translate-y-[1px] active:scale-[0.99]"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-heading text-lg font-bold tracking-tight">{b.name}</p>
                      <span
                        data-testid={`batch-status-${b.id}`}
                        className={`shrink-0 rounded-md border px-2 py-0.5 text-[10px] font-bold ${(BATCH_STATUS_META[b.operational_status] || BATCH_STATUS_META.open).badge}`}
                      >
                        {(BATCH_STATUS_META[b.operational_status] || BATCH_STATUS_META.open).label}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {b.region || b.destination || "—"}
                      {user.role !== "viewer" && b.exchange_rate_to_idr != null && ` · ${fmtRate(b.currency_symbol, b.exchange_rate_to_idr)}`}
                      {" · "}
                      {b.product_count} products
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      data-testid={`batch-summary-link-${b.id}`}
                      aria-label="Final purchasing summary"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/batches/${b.id}/summary`);
                      }}
                      className="flex h-9 w-9 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-rose-50 hover:text-rose-500"
                    >
                      <FileBarChart2 className="h-4 w-4" />
                    </button>
                    <ChevronRight className="h-5 w-5 text-stone-300" />
                  </div>
                </div>
                <div className="mt-3">
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-x-3 text-xs font-semibold text-stone-600">
                    <span data-testid={`batch-ordered-purchased-${b.id}`}>
                      Ordered {b.ordered_units} · Purchased {b.purchased_units}
                    </span>
                    <span className="flex gap-2">
                      {b.remaining_units > 0 && <span className="text-amber-600">Still to buy {b.remaining_units}</span>}
                      {b.buffer_units > 0 && <span className="text-emerald-600">Buffer {b.buffer_units}</span>}
                      {b.unassigned_count > 0 && (
                        <span data-testid={`batch-unassigned-${b.id}`} className="text-red-600">
                          {b.unassigned_count} unassigned
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-stone-200">
                    <div className="h-2 rounded-full bg-emerald-500 transition-[width] duration-300" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        data-testid="po-archive-link"
        onClick={() => navigate("/archive")}
        className="mt-4 flex w-full items-center gap-2.5 rounded-md border border-dashed bg-transparent px-4 py-3 text-left text-sm text-muted-foreground transition-colors hover:bg-accent/40"
      >
        <Archive className="h-4 w-4 shrink-0" />
        <span className="flex-1 font-semibold">PO Archive{archiveCount ? ` (${archiveCount})` : ""}</span>
        <ChevronRight className="h-4 w-4 shrink-0" />
      </button>

      <NewBatchDrawer open={newOpen} onOpenChange={setNewOpen} onSaved={load} />
      <CalculatorHub open={calcOpen} onOpenChange={setCalcOpen} />
    </div>
  );
}
